import { useTheme } from '../../components/ThemeProvider';
import {
  SCRIM_GRADIENT,
  VIGNETTE_GRADIENT,
  gradientCss,
  type QuoteArt as QuoteArtSpec,
} from '../../lib/quote/gradient';

/**
 * The card's art stack, bottom to top:
 *
 *   1. gradient      always rendered, always the base
 *   2. generated img fades in over it when a key is configured and it worked
 *   3. grain         inline SVG turbulence at 6%
 *   4. vignette      radial corner falloff
 *   5. scrim         the legibility layer the text sits on
 *
 * The gradient is the base rather than an either/or branch, which is what
 * makes every image failure mode invisible: a key that is unset, a request
 * that 401s, one that times out, or a payload that will not decode all land
 * on the same fully-designed card, with no flash of empty panel in between.
 *
 * The layers are decorative in the accessibility tree — the quote itself is
 * real text in `QuotePanel`, so screen readers get the content and none of
 * the scaffolding.
 */

/** Unique enough: exactly one Quote card exists in the grid. */
const GRAIN_FILTER_ID = 'quote-grain';

export function QuoteArtLayers({
  art,
  image,
  onImageError,
}: {
  art: QuoteArtSpec;
  image: string | null;
  onImageError: () => void;
}) {
  const { theme } = useTheme();

  return (
    <>
      {/*
        The no-key art, and the floor under every other state.
        `quote-placeholder` is asserted by the verification suite.
      */}
      <div
        data-testid="quote-placeholder"
        aria-hidden
        className="absolute inset-0"
        style={{ backgroundImage: gradientCss(art, theme) }}
      />

      {image !== null && (
        <img
          src={image}
          alt=""
          aria-hidden
          decoding="async"
          // A data: URL that will not decode fires this; the parent then
          // stops rendering the <img> entirely rather than leaving a broken
          // image icon sitting on the card.
          onError={onImageError}
          className="absolute inset-0 h-full w-full object-cover"
        />
      )}

      {/* Film grain: breaks up the banding a two-stop CSS gradient shows on
          a wide flat area, and is most of why the card reads as printed
          rather than as a default background. */}
      <svg
        aria-hidden
        className="pointer-events-none absolute inset-0 h-full w-full opacity-[0.06]"
        preserveAspectRatio="none"
      >
        <defs>
          <filter id={GRAIN_FILTER_ID}>
            <feTurbulence
              type="fractalNoise"
              baseFrequency="0.85"
              numOctaves={3}
              stitchTiles="stitch"
            />
            <feColorMatrix type="saturate" values="0" />
          </filter>
        </defs>
        <rect width="100%" height="100%" filter={`url(#${GRAIN_FILTER_ID})`} />
      </svg>

      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{ backgroundImage: VIGNETTE_GRADIENT }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{ backgroundImage: SCRIM_GRADIENT }}
      />
    </>
  );
}
